import { AdminPage } from "@/containers/admin/loadable";
import { CheckoutReturnPage } from "@/containers/checkout/loadable";
import { LandingPage } from "@/containers/landing/loadable";
import { MyFarmPage } from "@/containers/myFarm/loadable";
import { ParcelDetailPage } from "@/containers/parcelDetail/loadable";
import { ParcelsPage } from "@/containers/parcels/loadable";
import { TermsPage } from "@/containers/terms/loadable";
import { NotFound } from "@/shared/components/NotFound";
import { PageLayout } from "@/shared/components/PageLayout";
import { RequireAuth } from "@/shared/components/RequireAuth";
import { ScrollToTop } from "@/shared/components/ScrollToTop";
import { MY_FARM_HEADER_IMAGE } from "@/shared/constants/photos";
import { appRoutes, RouteNames } from "@/shared/constants/routes";
import { BrowserRouter, Route, Routes } from "react-router-dom";

/**
 * BrowserRouter, not HashRouter (SPEC §9): we control the server, and the
 * public catalogue needs real, shareable, indexable URLs. The Fastify service
 * must therefore fall back to `index.html` for unknown non-`/api` paths.
 */
function App() {
  return (
    <BrowserRouter>
      <ScrollToTop />

      <Routes>
        {/* Public — opens on the white produce hero, header overlaid on it
            in ink rather than white (the hero is no longer a photograph). */}
        <Route
          path={appRoutes[RouteNames.landing].path}
          element={
            <PageLayout heroBleed headerTone="dark">
              <LandingPage />
            </PageLayout>
          }
        />

        {/* Public catalogue */}
        <Route
          path={appRoutes[RouteNames.parcels].path}
          element={
            <PageLayout>
              <ParcelsPage />
            </PageLayout>
          }
        />
        <Route
          path={appRoutes[RouteNames.parcelDetail].path}
          element={
            <PageLayout heroBleed headerTone="light">
              <ParcelDetailPage />
            </PageLayout>
          }
        />

        {/* Return from Stripe. No footer — it is a transient status screen. */}
        <Route
          path={appRoutes[RouteNames.checkoutReturn].path}
          element={
            <PageLayout hideFooter>
              <RequireAuth>
                <CheckoutReturnPage />
              </RequireAuth>
            </PageLayout>
          }
        />

        {/* Buyer */}
        <Route
          path={appRoutes[RouteNames.myFarm].path}
          element={
            <PageLayout>
              {/* Same header photograph signed out as signed in — the page
                  must not change shape just because nobody is logged in.
                  Keep in step with MyFarmPage's own PageHero. */}
              <RequireAuth image={MY_FARM_HEADER_IMAGE}>
                <MyFarmPage />
              </RequireAuth>
            </PageLayout>
          }
        />

        {/*
          Ops. Auth-gated only — `isAdmin` is decided server-side from
          ADMIN_EMAILS, so the screen renders and handles a 403 itself rather
          than the client asserting a role it cannot verify.
        */}
        <Route
          path={appRoutes[RouteNames.admin].path}
          element={
            <PageLayout>
              <RequireAuth>
                <AdminPage />
              </RequireAuth>
            </PageLayout>
          }
        />

        {/* Legal. Public — a buyer must be able to read the terms before
            ordering, and Stripe's review looks for them on the site. */}
        <Route
          path={appRoutes[RouteNames.terms].path}
          element={
            <PageLayout>
              <TermsPage />
            </PageLayout>
          }
        />

        <Route
          path="*"
          element={
            <PageLayout>
              <NotFound />
            </PageLayout>
          }
        />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
